/* eslint-disable react-hooks/rules-of-hooks */
import { keyframes } from '@emotion/react'
import classNames from 'classnames'
import type { FC } from 'react'
import React, { useMemo } from 'react'

import type { SpinnerProps } from './types'
import useResponsiveValue from '../../../hooks/use-responsive-value'
import type { Theme } from '../../../theme-provider/types'
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui'
import { get } from '../../../utils/object'
import { resolveSize } from '../../../utils/size-utils'
import Box from '../box'
import Svg, { Circle } from '../svg'

const spin = keyframes({
  from: {
    transform: 'rotate(0deg)'
  },
  to: {
    transform: 'rotate(360deg)'
  }
})

const wrapperCss = {
  overflow: 'visible'
}

const Spinner: FC<SpinnerProps> = React.forwardRef<
  HTMLDivElement,
  SpinnerProps & { theme?: Theme }
>(function Spinner(props, ref) {
  const theme = useSafeThemeUI(props)
  const { className, size, strokeWidth, title, duration, ...rest } = props
  const r = 16 - (strokeWidth as number)
  const C = 2 * r * Math.PI
  const offset = C - (1 / 4) * C
  const spinnerSize = resolveSize(size, get(theme, 'sizes.spinner'))
  const _size = Array.isArray(spinnerSize)
    ? useResponsiveValue(spinnerSize)
    : spinnerSize

  const circleCss = useMemo(
    () => ({
      transformOrigin: '50% 50%',
      animationName: spin.toString(),
      animationTimingFunction: 'linear',
      animationDuration: duration + 'ms',
      animationIterationCount: 'infinite'
    }),
    [duration]
  )
  return (
    <Box
      className={classNames({ 'csg-tui-spinner': true }, className)}
      ref={ref}
      width={_size}
      height={_size}
      {...rest}
    >
      <Box
        className="csg-tui-spinner-auto-size"
        relative
        width="100%"
        mx={0}
        my="auto"
      >
        <Svg
          id="svg_spinner"
          viewBox="0 0 32 32"
          strokeWidth={strokeWidth}
          role="img"
          __css={wrapperCss}
        >
          <title>{title}</title>
          <circle cx={16} cy={16} r={r} opacity={1 / 4} />
          <Circle
            cx={16}
            cy={16}
            r={r}
            strokeDasharray={C}
            strokeDashoffset={offset}
            __css={circleCss}
          />
        </Svg>
      </Box>
    </Box>
  )
})

Spinner.defaultProps = {
  size: 'md',
  strokeWidth: 4,
  title: 'Loading...',
  duration: 1000
}

export default Spinner
